/**
 * The hover tooltip: what a hex is, what it produces, and what it costs.
 *
 * Replaces the fixed inspector panel, which required a click and then a look
 * away to the corner of the screen. Expansion is the central decision in the
 * game and it was being made blind — the cost of a hex only appeared *after*
 * committing attention to it. Putting the numbers under the cursor means the
 * decision and the information are in the same place.
 *
 * ## Everything here comes from the shared rules
 *
 * Yields come from `hexYield`, cost from `expansionCost`, affordability from
 * `canAfford` — the exact functions the server authorises with. The tooltip is
 * previewing the server's answer, never computing its own. A second
 * implementation of expansion cost would be a desync the player could see.
 */

import type {
  Biome,
  BuildingType,
  HexState,
  KingdomId,
  KingdomState,
  ResourceBundle,
  ResourceType,
  SpecialHex,
} from "kngdm-shared";
import {
  BUILDINGS,
  RESOURCE_TYPES,
  canAfford,
  expansionCost,
  hexYield,
  missingResources,
} from "kngdm-shared";

import { formatAmount } from "./format";

/** Offset from the cursor, so the pointer never sits on top of the text. */
const CURSOR_GAP = 18;

const BIOME_LABELS: Record<Biome, string> = {
  plains: "Plains",
  forest: "Forest",
  hills: "Hills",
  mountains: "Mountains",
  desert: "Desert",
  tundra: "Tundra",
};

const SPECIAL_LABELS: Record<SpecialHex, string> = {
  ruins: "Ancient ruins",
  geyser: "Geyser",
  crystalVein: "Crystal vein",
};

const RESOURCE_LABELS: Record<ResourceType, string> = {
  food: "Food",
  timber: "Timber",
  ore: "Ore",
  gold: "Gold",
  aether: "Aether",
};

export interface TooltipContext {
  readonly hex: HexState;
  readonly ownKingdomId: KingdomId | null;
  readonly kingdoms: readonly KingdomState[];
  /** Territory the player holds, for the expansion cost curve. */
  readonly territoryCount: number;
  readonly resources: ResourceBundle;
  /** Whether this hex is adjacent to the player's land. */
  readonly adjacent: boolean;
}

export class HexTooltip {
  readonly element: HTMLElement;

  private readonly title: HTMLElement;
  private readonly subtitle: HTMLElement;
  private readonly yields: HTMLElement;
  private readonly facts: HTMLElement;
  private readonly costBlock: HTMLElement;
  private readonly costRow: HTMLElement;
  private readonly costNote: HTMLElement;

  private visible = false;

  constructor() {
    this.element = el("div", "tip is-hidden");

    this.title = el("div", "tip__title");
    this.subtitle = el("div", "tip__subtitle");
    this.yields = el("div", "tip__yields");
    this.facts = el("dl", "tip__facts");

    this.costBlock = el("div", "tip__cost");
    this.costRow = el("div", "tip__costrow");
    this.costNote = el("div", "tip__costnote");
    this.costBlock.append(el("div", "tip__costlabel", "Cost to claim"), this.costRow, this.costNote);

    this.element.append(this.title, this.subtitle, this.yields, this.facts, this.costBlock);
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.add("is-hidden");
  }

  /** Position against the cursor, flipping before it would leave the viewport. */
  moveTo(screenX: number, screenY: number): void {
    const rect = this.element.getBoundingClientRect();
    const flipX = screenX + CURSOR_GAP + rect.width > window.innerWidth;
    const flipY = screenY + CURSOR_GAP + rect.height > window.innerHeight;

    const x = flipX ? screenX - CURSOR_GAP - rect.width : screenX + CURSOR_GAP;
    const y = flipY ? screenY - CURSOR_GAP - rect.height : screenY + CURSOR_GAP;

    this.element.style.transform = `translate(${Math.max(4, x)}px, ${Math.max(4, y)}px)`;
  }

  show(context: TooltipContext): void {
    const { hex } = context;

    this.title.textContent = BIOME_LABELS[hex.biome];

    const owner = context.kingdoms.find((k) => k.id === hex.owner);
    const ownerLabel =
      hex.owner === null
        ? "Unclaimed"
        : hex.owner === context.ownKingdomId
          ? "Your territory"
          : (owner?.name ?? "Rival territory");
    this.subtitle.textContent = hex.special === null
      ? ownerLabel
      : `${SPECIAL_LABELS[hex.special]} · ${ownerLabel}`;
    this.subtitle.classList.toggle("is-special", hex.special !== null);

    this.renderYields(hex.biome, hex.building, hex.special);
    this.renderFacts(hex);
    this.renderCost(context);

    if (!this.visible) {
      this.visible = true;
      this.element.classList.remove("is-hidden");
    }
  }

  /**
   * Per-second output at full worker coverage.
   *
   * Stated as the hex's own potential rather than the player's actual rate,
   * because worker coverage is a kingdom-wide property — folding it in here
   * would make the same hex show different numbers depending on how much
   * unrelated land you happened to own, which is not what the player is asking.
   */
  private renderYields(
    biome: Biome,
    building: BuildingType | null,
    special: SpecialHex | null,
  ): void {
    const output = hexYield(biome, building, special);
    this.yields.replaceChildren();

    let any = false;
    for (const type of RESOURCE_TYPES) {
      if (output[type] <= 0) continue;
      any = true;
      const cell = el("span", `tip__yield tip__yield--${type}`);
      cell.append(
        el("span", "tip__yieldlabel", RESOURCE_LABELS[type]),
        el("span", "tip__yieldvalue", `${output[type].toFixed(2)}/s`),
      );
      this.yields.append(cell);
    }

    if (!any) this.yields.append(el("span", "tip__barren", "Produces nothing"));
  }

  private renderFacts(hex: HexState): void {
    this.facts.replaceChildren();

    if (hex.building !== null) {
      appendFact(this.facts, "Building", BUILDINGS[hex.building].label);
    }
    if (hex.troops >= 0.05) {
      appendFact(this.facts, "Troops", formatAmount(hex.troops));
    }
    if (hex.capture > 0.001) {
      appendFact(this.facts, "Contested", `${Math.round(hex.capture * 100)}%`);
    }
    if (hex.fortified) appendFact(this.facts, "Fortified", "Yes");
  }

  private renderCost(context: TooltipContext): void {
    const { hex } = context;

    // Cost is only meaningful for land that can actually be bought. Owned hexes
    // are not for sale at any price, and enemy land changes hands through
    // troops rather than resources.
    if (hex.owner !== null || context.ownKingdomId === null) {
      this.costBlock.classList.add("is-hidden");
      return;
    }
    this.costBlock.classList.remove("is-hidden");

    const cost = expansionCost(hex.biome, context.territoryCount);
    const affordable = canAfford(context.resources, cost);
    const missing = new Set<ResourceType>(missingResources(context.resources, cost));

    this.costRow.replaceChildren();
    for (const type of RESOURCE_TYPES) {
      if (cost[type] <= 0) continue;
      const chip = el("span", "tip__chip", `${formatAmount(cost[type])} ${RESOURCE_LABELS[type]}`);
      chip.classList.toggle("is-missing", missing.has(type));
      this.costRow.append(chip);
    }

    if (!context.adjacent) {
      this.costNote.textContent = "Not adjacent to your territory";
      this.costNote.className = "tip__costnote is-blocked";
    } else if (!affordable) {
      this.costNote.textContent = "You cannot afford this";
      this.costNote.className = "tip__costnote is-blocked";
    } else {
      this.costNote.textContent = "Click to claim";
      this.costNote.className = "tip__costnote is-ready";
    }
  }
}

function appendFact(list: HTMLElement, label: string, value: string): void {
  list.append(el("dt", undefined, label), el("dd", undefined, value));
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
